import React, { useState } from 'react';
import SafetyNote from './SafetyNote';
import {
  Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
  InputLabel, MenuItem, Rating, Select, Stack, TextField, Typography,
} from '@mui/material';
import { postService, Post, WaterCurrent } from '../services/postService';
import { OptionsManager } from '../utils/optionsManager';
import { CURRENT_OPTIONS, RATING_LABELS, tempToCelsius, visibilityToMeters } from '../utils/conditions';
import { useNotification } from './NotificationContext';

interface ConditionsReportDialogProps {
  site: { slug: string; name: string };
  onClose: () => void;
  onReported: (post: Post) => void;
}

/**
 * "How were conditions?": a few structured fields so reports can be compared and summarized on the
 * site page. Typed in the diver's own units, stored metric. Always Public — the site hub only ever
 * shows what its authors chose to share.
 */
const ConditionsReportDialog: React.FC<ConditionsReportDialogProps> = ({ site, onClose, onReported }) => {
  const { showSuccess, showError } = useNotification();
  const units = OptionsManager.getInstance().getOption('units');
  const [visibility, setVisibility] = useState('');
  const [temperature, setTemperature] = useState('');
  const [current, setCurrent] = useState<WaterCurrent | ''>('');
  const [rating, setRating] = useState<number | null>(null);
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const visNumber = visibility.trim() === '' ? null : Number(visibility);
  const tempNumber = temperature.trim() === '' ? null : Number(temperature);
  const visInvalid = visNumber !== null && (Number.isNaN(visNumber) || visNumber < 0);
  const tempInvalid = tempNumber !== null && Number.isNaN(tempNumber);
  const hasAnything = visNumber !== null || tempNumber !== null || current !== '' || rating !== null;
  const canSubmit = hasAnything && !visInvalid && !tempInvalid && !submitting;

  const depthLabel = units.depth === 'feet' ? 'ft' : 'm';
  const tempLabel = units.temperature === 'fahrenheit' ? '°F' : '°C';

  const submit = async () => {
    setSubmitting(true);
    try {
      const post = await postService.createConditionsReport({
        diveSiteSlug: site.slug,
        diveSiteName: site.name,
        visibilityMeters: visNumber === null ? undefined : Math.round(visibilityToMeters(visNumber, units.depth) * 10) / 10,
        waterTemperatureC: tempNumber === null ? undefined : Math.round(tempToCelsius(tempNumber, units.temperature) * 10) / 10,
        current: current || undefined,
        rating: rating ?? undefined,
        note: note.trim() || undefined,
      });
      showSuccess(`Thanks — your report for ${site.name} is up`);
      onReported(post);
      onClose();
    } catch (err) {
      showError(err instanceof Error && err.message ? err.message : 'Failed to post your report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onClose={submitting ? undefined : onClose} fullWidth maxWidth="xs">
      <DialogTitle>Report conditions at {site.name}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            Fill in what you noticed — everything is optional, but add at least one. Reports are public.
          </Typography>
          <Stack direction="row" spacing={1.5}>
            <TextField
              label={`Visibility (${depthLabel})`}
              type="number"
              size="small"
              fullWidth
              value={visibility}
              error={visInvalid}
              onChange={(e) => setVisibility(e.target.value)}
              inputProps={{ min: 0, step: 'any', inputMode: 'decimal' }}
            />
            <TextField
              label={`Water temp (${tempLabel})`}
              type="number"
              size="small"
              fullWidth
              value={temperature}
              error={tempInvalid}
              onChange={(e) => setTemperature(e.target.value)}
              inputProps={{ step: 'any', inputMode: 'decimal' }}
            />
          </Stack>
          <FormControl size="small" fullWidth>
            <InputLabel>Current</InputLabel>
            <Select value={current} label="Current" onChange={(e) => setCurrent(e.target.value as WaterCurrent | '')}>
              <MenuItem value=""><em>Not sure</em></MenuItem>
              {CURRENT_OPTIONS.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </Select>
          </FormControl>
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>Overall, how were conditions?</Typography>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Rating value={rating} onChange={(_, v) => setRating(v)} size="large" />
              {rating && <Typography variant="body2">{RATING_LABELS[rating]}</Typography>}
            </Stack>
          </Box>
          <TextField
            label="Anything else? (optional)"
            placeholder="Surge, marine life, entry/exit, what to watch for…"
            value={note}
            onChange={(e) => setNote(e.target.value.slice(0, 280))}
            multiline
            minRows={2}
            helperText={`${note.length}/280`}
          />
          <SafetyNote kind="conditions" />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!canSubmit}>
          {submitting ? <CircularProgress size={20} /> : 'Post report'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConditionsReportDialog;
